import { useEffect } from "react";
import { useRouteError, useNavigate } from "react-router-dom";
import { IconAlertTriangle } from "@tabler/icons-react";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { describeRouteError, logRouteError } from "./logRouteError";

/**
 * What a page renders instead of itself when it throws. It sits inside the App
 * layout's Outlet, so the header and sidebar survive and the operator can go
 * somewhere else rather than reaching for the back button — the point of
 * catching here rather than above the router, where React Router's own blank
 * "Unexpected Application Error!" screen used to take over the whole window.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { title, detail } = describeRouteError(error);

  // In an effect, not the render body: StrictMode renders twice in dev, and
  // this is the seam a reporting service will sit behind, where a duplicate
  // report per crash is noise worth paying for once.
  useEffect(() => {
    logRouteError(error, "RouteErrorPage");
  }, [error]);

  return (
    <Empty className="min-h-[60vh]">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <IconAlertTriangle />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>
          Nothing you did caused this, and nothing you had saved is lost. Try
          again, and let support know if it keeps happening.
        </EmptyDescription>
      </EmptyHeader>
      {detail && (
        <pre className="mx-auto max-w-xl overflow-x-auto rounded-lg bg-muted px-4 py-3 text-left text-xs text-text-muted">
          {detail}
        </pre>
      )}
      <EmptyContent className="flex-row justify-center gap-2">
        {/* A reload, not a re-render: the route already threw once, and its
            loaders should run again from a clean slate. */}
        <Button
          className="text-xs rounded-lg"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
        <Button
          variant="outline"
          className="text-xs rounded-lg"
          onClick={() => void navigate("/dashboard")}
        >
          Go to Dashboard
        </Button>
      </EmptyContent>
    </Empty>
  );
}
